import { PAGE_TYPES } from '../enums/page-types';
import { getMinimumChargeAmount, isBelowMinimumChargeAmount } from '../services/currency';
import { formatPrice, formatPriceInput } from '../services/formatPrice';
import checkIsVariantActive from '../lib/checkIsVariantActive';
import checkIsVariantEnabled from '../lib/checkIsVariantEnabled';
import checkIsVariantVisible from '../lib/checkIsVariantVisible';
import checkIsVariantForPrice from '../lib/checkIsVariantForPrice';
import { htmlToPlainText } from '../lib/htmlToPlainText';
import hasStock from '../lib/hasStock';
import isPwywOption from '../lib/isPwywOption';
import getSelectedTicketTypes from '../lib/getSelectedTicketTypes';
import getSelectedOrderBumps from '../lib/getSelectedOrderBumps';
import useCheckoutState from './useCheckoutState';
import useSelectedPrice from './useSelectedPrice';
import getThemeLabels from '../lib/getThemeLabels';

const useCheckoutValidation = () => {
  const { checkout, product, form, query, theme, effectiveSelectedPriceId } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  const labels = getThemeLabels(theme);

  function validateMinimumChargeAmount(amount, currency, locale) {
    if (isBelowMinimumChargeAmount(amount, currency)) {
      throw new Error(
        `The minimum transaction amount in ${currency.toUpperCase()} is ${formatPrice({
          amount: getMinimumChargeAmount(currency),
          currency,
          locale,
        })}`,
      );
    }
  }

  function validateHasStock() {
    if (checkout.type !== PAGE_TYPES.CHECKOUT) return;

    if (!hasStock(product)) {
      throw new Error('This item is sold out.');
    }
  }

  function validateVariants() {
    if (product.variants && product.variants.length > 0) {
      // validate if all required variants have a value in form.variants and are visible
      const requiredVariants = product.variants.filter(variant => {
        const enabled = checkIsVariantEnabled(variant, form.variants, product.variants);

        const visible = checkIsVariantVisible(variant, query);

        const inPriceScope = checkIsVariantForPrice(variant, effectiveSelectedPriceId);

        return (
          variant.required && checkIsVariantActive(variant) && enabled && visible && inPriceScope
        );
      });

      const requiredVariantsWithoutQuantity = requiredVariants.filter(variant => {
        const selectedOptions = form.variants[variant._id];

        if (!selectedOptions || !selectedOptions.length) return true;

        const hasSelectedOptionWithQuantity = selectedOptions.find(option => option.quantity > 0);

        return !hasSelectedOptionWithQuantity;
      });

      if (requiredVariantsWithoutQuantity.length > 0) {
        throw new Error(
          `${htmlToPlainText(requiredVariantsWithoutQuantity[0].name)} ${labels.variantIsRequiredText}`,
        );
      }

      requiredVariants.forEach(variant => {
        const selectedOptions = form.variants[variant._id];
        if (!selectedOptions || !selectedOptions.length) return;

        variant.options.forEach(option => {
          if (!isPwywOption(option)) return;

          const formOption = selectedOptions.find(so => so.optionId === option._id);
          if (!formOption || formOption.quantity === 0) return;

          const minimumPrice = option.additionalChargeAmount || 0;
          const enteredAmount =
            formOption.payWhatYouWantAmount != null && formOption.payWhatYouWantAmount !== ''
              ? Number(formatPriceInput(formOption.payWhatYouWantAmount, product.currency))
              : 0;

          if (enteredAmount < minimumPrice) {
            throw new Error(
              `${htmlToPlainText(option.name)} amount must be at least ${formatPrice({
                amount: minimumPrice,
                currency: product.currency,
                style: 'currency',
              })}`,
            );
          }
        });
      });
    }
  }

  function validateTickets() {
    if (checkout.type !== PAGE_TYPES.EVENT) return;

    const currency = checkout.eventDetails?.currency || product?.currency;
    // With currency passed, each entry's `price` is the resolved entered
    // amount and `originalPrice` is the stored minimum — the same resolution
    // (getResolvedTicketPrice) that feeds the cart total and the request.
    const selectedTicketTypes = getSelectedTicketTypes(
      checkout.ticketGroups,
      form.ticketGroups,
      currency,
    );

    if (checkout.ticketGroups?.length > 0 && selectedTicketTypes.length === 0) {
      throw new Error('Select at least one ticket.');
    }

    selectedTicketTypes.forEach(ticketType => {
      if (ticketType.pricing !== 'pwyw') return;

      const minimumPrice = ticketType.originalPrice || 0;
      const enteredAmount = ticketType.price;

      if (enteredAmount < minimumPrice) {
        throw new Error(
          `${htmlToPlainText(ticketType.name)} amount must be at least ${formatPrice({
            amount: minimumPrice,
            currency,
            style: 'currency',
          })}`,
        );
      }
    });
  }

  // The server requires an amount on every PWYW bump, even one with a floor of 0.
  function validateOrderBumps() {
    const selectedOrderBumps = getSelectedOrderBumps({
      page: checkout,
      product,
      selectedPrice,
      form,
      currency: checkout.eventDetails?.currency || product?.currency,
    });

    selectedOrderBumps.forEach(({ offer, payWhatYouWantAmount }) => {
      if (!offer.price.payWhatYouWant) return;

      if (payWhatYouWantAmount == null) {
        throw new Error(`Enter an amount for ${offer.title}.`);
      }

      const minimumPrice = offer.price.amount || 0;

      if (payWhatYouWantAmount < minimumPrice) {
        throw new Error(
          `${offer.title} amount must be at least ${formatPrice({
            amount: minimumPrice,
            currency: offer.currency,
            style: 'currency',
          })}`,
        );
      }
    });
  }

  return {
    validateMinimumChargeAmount,
    validateHasStock,
    validateVariants,
    validateTickets,
    validateOrderBumps,
  };
};

export default useCheckoutValidation;
